<script setup lang="ts">
import type { Media } from "@/models"
import { computed } from "vue"
import { useDeleteMultipleMedia } from "@/api"
import { MediaTile } from "@/components"
import { BaseModal, Button, Card, Icon } from "@/components/ui"
import { useModal } from "@/composables"
import { useTranslation } from "@/composables/useTranslation"
import { MediaType } from "@/models"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { MediaDeleteModalKey } from "@/symbols"

interface MediaDeleteModalProps {
  media?: Media[]
}

const props = withDefaults(defineProps<MediaDeleteModalProps>(), { media: () => [] })

const emit = defineEmits<{
  (e: "success"): void
  (e: "error", error: unknown): void
}>()

const { t } = useTranslation()

const eventStore = useEventVaultStore()

const { toggle } = useModal(MediaDeleteModalKey)

const picIds = computed<number[]>(() => {
  return props.media.reduce<number[]>((result, m) => {
    if (m.type === MediaType.Pic && m.id)
      result.push(m.id)

    return result
  }, [])
})

const vidIds = computed<number[]>(() => {
  return props.media.reduce<number[]>((result, m) => {
    if (m.type === MediaType.Vid && m.id)
      result.push(m.id)

    return result
  }, [])
})

const { mutate, isPending } = useDeleteMultipleMedia()

function onConfirm() {
  if (!eventStore.eventVaultId) {
    throw new Error(`Missing eventVaultId when attempting to delete media.`)
  }

  mutate({ picIds: picIds.value, vidIds: vidIds.value, eventVaultId: eventStore.eventVaultId }, {
    onSuccess: () => {
      emit("success")
      toggle()
    },
    onError: error => emit("error", error),
  })
}
</script>

<template>
  <BaseModal :name="MediaDeleteModalKey">
    <Card>
      <transition name="gpx-fade" mode="out-in">
        <div v-if="isPending" class="gpx-delete-media_loading">
          <div class="gpx-delete-media-header">
            <div class="gpx-skeleton gpx-delete-media-heading_loading" />
            <Button variant="flat" @click="toggle">
              <span class="sr-only">{{ t("close") }}</span>
              <Icon icon="Close" />
            </Button>
          </div>
          <div class="gpx-delete-media-message">
            <div class="gpx-skeleton gpx-delete-media-icon_loading" />
            <div class="gpx-skeleton gpx-delete-media-message_loading" />
          </div>
          <div class="flex flex-col gap-2 mt-2 md:flex-row">
            <div class="gpx-skeleton gpx-delete-media-action_loading" />
            <div class="gpx-skeleton gpx-delete-media-action_loading" />
          </div>
        </div>
        <div v-else-if="media" class="gpx-delete-media">
          <div class="gpx-delete-media-header">
            <h2>{{ t("media_delete_modal.title") }}</h2>
            <Button variant="flat" @click="toggle">
              <span class="sr-only">{{ t("close") }}</span>
              <Icon icon="Close" />
            </Button>
          </div>
          <div>
            <div class="flex gap-4 items-center">
              <Icon icon="Caution" size="lg" class="text-red-700 shrink-0" />
              <p>
                {{ t("media_delete_modal.message") }}
              </p>
            </div>
            <div class="grid gap-3 grid-cols-1 md:grid-cols-2 mt-6">
              <MediaTile
                v-for="(item, index) in media?.slice(0, 3)" :key="`media-${index}`"
                :media="item"
              />
              <div
                v-if="media?.slice(3).length"
                class="pl-4 border-gray-300 gap-4 flex items-center font-montserrat font-medium"
              >
                <div>
                  <h5 class="text-gray-800 mb-1 line-clamp-2 text-ellipsis">
                    {{ t("media_delete_modal.plus_others", { count: media.slice(3).length }) }}
                  </h5>
                </div>
              </div>
            </div>
            <div class="flex flex-col gap-2 mt-8 md:flex-row justify-end">
              <Button size="lg" variant="danger" @click="onConfirm">
                {{ t("media_delete_modal.confirm") }}
              </Button>
              <Button size="lg" variant="flat" @click="toggle">
                {{ t("media_delete_modal.decline") }}
              </Button>
            </div>
          </div>
        </div>
      </transition>
    </Card>
  </BaseModal>
</template>

<style scoped>
.gpx-delete-media_loading {
  min-width: 300px;
}

.gpx-delete-media-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.gpx-delete-media-header .gpx-button {
  padding: 0;
}

.gpx-delete-media-header h2 {
  font-size: 1.25rem;
  white-space: nowrap;
  margin: 0;
}

.gpx-delete-media-message {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.gpx-delete-media-heading_loading {
  width: 12rem;
  height: 2.5rem;
}

.gpx-delete-media-icon_loading {
  width: 4rem;
  height: 4rem;
}

.gpx-delete-media-message_loading {
  width: 12rem;
  height: 3rem;
}

.gpx-delete-media-action_loading {
  width: 8rem;
  height: 3rem;
}

@media (width >=768px) {
  .gpx-delete-media-header h2 {
    font-size: 1.5rem;
  }
}
</style>
